import {useConfig} from '@aboutyou/config';
import {
  InspirationGridSection_Element,
  InspirationGridSection as InspirationGridSectionType,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation_page/sections.types';
import {
  originClick,
  TrackingPromotionCreative,
} from '@aboutyou/legacy-tracking-sdk';
import {TrackPromotions, useTrackOriginClick} from '@aboutyou/tracking';
import {
  CardImageWithFooter,
  CardImageWithFooterImage,
  COLORS,
  desktopAndUpLinaria,
  FONT_SIZES,
  mobileOnlyLinaria,
  RowSection,
  tabletAndUpLinaria,
  Text,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {TextBanner} from '../../../../features/Recommendations/deprecated/shared';
import {InspirationGridSectionButton} from './InspirationGridSectionButton';
import {InspirationGridSectionTeaserCaption} from './InspirationGridSectionTeaserCaption';

export const InspirationGridSection: React.FC<
  InspirationGridSectionType & {streamPosition: number}
> = ({streamPosition, headline, subline, elements, teaser}) => {
  const {device} = useConfig();
  const trackOriginClick = useTrackOriginClick();

  const onCardClick = (locationId?: string) => {
    if (locationId) {
      trackOriginClick(locationId as originClick)();
    }
  };

  return (
    <RowSection data-testid="inspirationGridSection">
      <StyledTextBanner headline={headline} subline={subline} />
      <Wrapper>
        {elements.map((element, index) => {
          const {href, image, ariaLabel} = getCardContent(element.type!);
          return device === 'desktop' && image ? (
            <StyledCardImageWithFooter
              key={index}
              href={href}
              aria-label={ariaLabel}
              image={image}
              imageRatio="3:4"
              breakpoints={[]}
              footer={<CardImageFooter key={index} type={element.type} />}
            />
          ) : (
            <InspirationGridSectionButton
              key={index}
              type={element.type}
              href={href}
            />
          );
        })}
        {teaser && (
          <TrackPromotions
            streamPosition={streamPosition}
            promotion={{
              source: TrackingPromotionCreative.GenericPromotion,
              promotionTracker: {
                ...teaser.promotionTracker,
                destination: teaser.link.url,
              },
            }}
            triggerOnce
          >
            <StyledCardImageWithFooter
              data-testid="inspirationGridSectionTeaser"
              href={teaser.link.url}
              onClick={() => onCardClick(teaser.promotionTracker.locationId)}
              aria-label={teaser.headline}
              image={
                device === 'desktop'
                  ? teaser.coverPortraitImage
                  : teaser.coverSquareImage
              }
              imageRatio={device === 'desktop' ? '3:4' : '1:1'}
              breakpoints={[]}
              caption={
                <InspirationGridSectionTeaserCaption liveAt={teaser.liveAt} />
              }
              footer={
                <Footer>
                  <Text color="Text.Grey" weight="mediumBold">
                    {teaser.subline}
                  </Text>
                  <Text size="px16" weight="mediumHeavy">
                    {teaser.headline}
                  </Text>
                </Footer>
              }
            />
          </TrackPromotions>
        )}
      </Wrapper>
    </RowSection>
  );
};

const getCardContent = (
  type: NonNullable<InspirationGridSection_Element['type']>,
) => {
  switch (type.$case) {
    case 'liveShopping':
      return {
        ariaLabel: type.liveShopping.headline,
        href: type.liveShopping.link.url,
        image: type.liveShopping.portraitImage,
      };
    case 'limitedDrops':
      return {
        ariaLabel: type.limitedDrops.headline,
        href: type.limitedDrops.link.url,
        image: type.limitedDrops.portraitImage,
      };
    case 'sneakerReleaseCalendar':
      return {
        ariaLabel: type.sneakerReleaseCalendar.headline,
        href: type.sneakerReleaseCalendar.link.url,
        image: type.sneakerReleaseCalendar.portraitImage,
      };
  }
};

const StyledTextBanner = styled(TextBanner)`
  ${mobileOnlyLinaria(`margin-left: 16px;`)}
`;

const Wrapper = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, 1fr);
  padding-inline: 16px;
  padding-bottom: 10px;
  width: 100%;

  // Make elements after the first 2 span full width on mobile
  & > *:nth-child(n + 3) {
    grid-column: 1 / -1;
  }

  // On tablet and up, use single row layout with equal widths
  ${tabletAndUpLinaria(`
    margin-top: 24px;
    padding: 0;
    
    grid-template-columns: repeat(4, 1fr);
    & > *:nth-child(n + 3) {
      grid-column: auto;
    }
  `)}
`;

const StyledCardImageWithFooter = styled(CardImageWithFooter)`
  ${CardImageWithFooterImage} {
    ${mobileOnlyLinaria(`max-height: 260px;`)}
  }

  ${tabletAndUpLinaria(`
    &&& {
      box-shadow: none;
    }
  `)}
`;

const CardImageFooter = styled(InspirationGridSectionButton)``;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;

  > span {
    font-size: ${FONT_SIZES.px16};
  }

  ${tabletAndUpLinaria(`
    height: 68px;
    background-color: ${COLORS['Fill.Grey.Primary']};

    > span {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;

      &:first-child {
        font-size: ${FONT_SIZES.px10};
      }

      &:last-child {
        font-size: ${FONT_SIZES.px12};
      }
    }
  `)}

  ${desktopAndUpLinaria(`
    > span:first-child {
      font-size: ${FONT_SIZES.px14};
    }

    > span:last-child {
      font-size: ${FONT_SIZES.px16};
    }
  `)}
`;
